import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Package, History } from "lucide-react";
import {
  AdminDataTable,
  type ColumnDef,
  SearchInput,
  FilterBar,
  type FilterConfig,
  EmptyState,
  ExportButton,
} from "@/components/admin/shared";
import { StockStatusBadge } from "./StockStatusBadge";
import { MovementHistoryModal } from "./MovementHistoryModal";
import { useInventoryStock } from "@/hooks/admin/useInventory";
import type { InventoryProduct, StockStatus } from "@/lib/admin-api/inventory";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: "in_stock", label: "En stock" },
  { value: "low_stock", label: "Faible" },
  { value: "out_of_stock", label: "Rupture" },
] as const;

const FILTER_CONFIGS: readonly FilterConfig[] = [
  {
    key: "status",
    label: "Statut",
    type: "select",
    options: STATUS_OPTIONS,
  },
];

const EXPORT_COLUMNS = [
  { key: "name", label: "Produit" },
  { key: "sku", label: "SKU" },
  { key: "brand", label: "Marque" },
  { key: "category", label: "Catégorie" },
  { key: "current_stock", label: "Stock actuel" },
  { key: "alert_threshold", label: "Seuil d'alerte" },
  { key: "status", label: "Statut" },
] as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StockCurrentTab() {
  const { t } = useTranslation("admin");

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StockStatus | null>(null);
  const [historyProduct, setHistoryProduct] = useState<InventoryProduct | null>(null);

  const queryParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      q: search || null,
      status: statusFilter,
    }),
    [page, search, statusFilter],
  );

  const { data, isLoading, isError } = useInventoryStock(queryParams);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    if (key === "status") {
      setStatusFilter((value as StockStatus | null) ?? null);
    }
    setPage(1);
  }, []);

  const handleShowHistory = useCallback((product: InventoryProduct) => {
    setHistoryProduct(product);
  }, []);

  const handleCloseHistory = useCallback(() => {
    setHistoryProduct(null);
  }, []);

  const products = useMemo(() => data?.products ?? [], [data?.products]);
  const pagination = data?.pagination;

  const exportData = useMemo(
    () =>
      products.map((p) => ({
        name: p.name,
        sku: p.sku,
        brand: p.brand,
        category: p.category,
        current_stock: p.current_stock,
        alert_threshold: p.alert_threshold,
        status: p.status,
      })),
    [products],
  );

  const columns: readonly ColumnDef<InventoryProduct>[] = useMemo(
    () => [
      {
        id: "product",
        header: t("inventory.col_product", "Produit"),
        cell: (p) => (
          <span className="flex items-center gap-2">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-stone-100 text-stone-500">
              <Package className="h-3.5 w-3.5" />
            </span>
            <span>
              <span className="block text-sm font-medium text-stone-800">
                {p.name}
              </span>
              <span className="block text-xs text-stone-500">{p.brand}</span>
            </span>
          </span>
        ),
      },
      {
        id: "sku",
        header: t("inventory.col_sku", "SKU"),
        mobileVisible: false,
        cell: (p) => (
          <span className="font-mono text-xs text-stone-600">{p.sku}</span>
        ),
      },
      {
        id: "stock",
        header: t("inventory.col_stock", "Stock actuel"),
        sortable: true,
        cell: (p) => (
          <span className="tabular-nums font-semibold text-stone-800">
            {p.current_stock}
          </span>
        ),
      },
      {
        id: "threshold",
        header: t("inventory.col_threshold", "Seuil d'alerte"),
        mobileVisible: false,
        cell: (p) => (
          <span className="tabular-nums text-sm text-stone-500">
            {p.alert_threshold}
          </span>
        ),
      },
      {
        id: "updated_at",
        header: t("inventory.col_updated", "Mis à jour"),
        mobileVisible: false,
        cell: (p) => (
          <span className="text-xs text-stone-400">
            {format(new Date(p.updated_at), "dd MMM yyyy", { locale: fr })}
          </span>
        ),
      },
      {
        id: "status",
        header: t("inventory.col_status", "Statut"),
        cell: (p) => <StockStatusBadge status={p.status} />,
      },
      {
        id: "actions",
        header: "",
        mobileVisible: false,
        cell: (p) => (
          <button
            onClick={() => handleShowHistory(p)}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-stone-500 hover:bg-stone-100 hover:text-stone-700"
            aria-label={t("inventory.view_history", "Voir l'historique")}
          >
            <History className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">
              {t("inventory.view_history", "Historique")}
            </span>
          </button>
        ),
      },
    ],
    [t, handleShowHistory],
  );

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="w-full sm:max-w-xs">
            <SearchInput
              value={search}
              onChange={handleSearch}
              placeholder={t(
                "inventory.search_placeholder",
                "Rechercher par nom, SKU…",
              )}
            />
          </div>
          <FilterBar
            filters={FILTER_CONFIGS}
            values={{ status: statusFilter }}
            onChange={handleFilterChange}
          />
        </div>
        <ExportButton
          data={exportData}
          columns={[...EXPORT_COLUMNS]}
          filename="stock-actuel-djayma"
        />
      </div>

      {/* Error */}
      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {t("inventory.error_loading", "Impossible de charger l'inventaire.")}
        </div>
      )}

      {/* Table */}
      {!isLoading && products.length === 0 ? (
        <EmptyState
          icon={<Package className="h-6 w-6" />}
          title={t("inventory.empty_title", "Aucun produit trouvé")}
          description={t(
            "inventory.empty_description",
            "Modifiez vos filtres ou ajoutez des produits au catalogue.",
          )}
        />
      ) : (
        <AdminDataTable<InventoryProduct>
          columns={columns}
          data={products}
          isLoading={isLoading}
          pagination={pagination}
          onPageChange={setPage}
          emptyMessage={t("inventory.empty_title", "Aucun produit")}
        />
      )}

      {/* Movement history modal */}
      <MovementHistoryModal
        product={historyProduct}
        onClose={handleCloseHistory}
      />
    </div>
  );
}
